/**
 * ui-renderers/workflows.ts
 * Scheduled workflow card list.
 */

import { dom } from '../dom';
import { escapeHtml, escapeAttr, formatRelativeTime } from './core';

export const CRON_LABELS: Record<string, string> = {
  '0 9 * * 1':   'Every Monday at 9 am',
  '0 9 * * *':   'Daily at 9 am',
  '0 9 * * 5':   'Every Friday at 9 am',
  '0 9 * * 1-5': 'Weekdays at 9 am',
  '0 8 * * 1':   'Every Monday at 8 am',
  '0 0 * * *':   'Daily at midnight',
};

export const WF_TYPE_LABELS: Record<string, string> = {
  case_law_alert:   '⚖️ Case Law Alert',
  deadline_summary: '📅 Deadline Summary',
  custom_prompt:    '✏️ Custom Prompt',
};

export const WF_STATUS_META: Record<string, { cls: string; label: string }> = {
  active:   { cls: 'wf-status-active',   label: 'Active'   },
  paused:   { cls: 'wf-status-paused',   label: 'Paused'   },
  disabled: { cls: 'wf-status-disabled', label: 'Disabled' },
};

export function renderWorkflowsList(workflows: any[]) {
  const list = dom.wfList;
  if (!list) return;

  if (!workflows || workflows.length === 0) {
    list.innerHTML = [
      '<div class="ai-panel-empty">',
        'No scheduled workflows yet.<br>',
        'Tap <strong>+</strong> above to create one.',
      '</div>',
    ].join('');
    return;
  }

  list.innerHTML = '';
  workflows.forEach(wf => {
    const typeLabel  = WF_TYPE_LABELS[wf.workflow_type] || wf.workflow_type || 'Workflow';
    const cronLabel  = CRON_LABELS[wf.cron_schedule] || wf.cron_schedule || '—';
    const statusMeta = WF_STATUS_META[wf.status] || WF_STATUS_META.active;
    const created    = wf.created_at ? formatRelativeTime(wf.created_at) : '';

    const card = document.createElement('div');
    card.className = 'ai-wf-card';
    card.dataset.wfId = wf.id;
    card.innerHTML = [
      '<div class="ai-wf-card-header">',
        '<span class="ai-wf-card-name">' + escapeHtml(wf.name || 'Unnamed') + '</span>',
        '<span class="ai-wf-card-status ' + statusMeta.cls + '">' + statusMeta.label + '</span>',
      '</div>',
      '<div class="ai-wf-card-meta">',
        '<span class="ai-wf-card-type">' + typeLabel + '</span>',
        '<span class="ai-wf-card-sep">·</span>',
        '<span class="ai-wf-card-cron">' + escapeHtml(cronLabel) + '</span>',
      '</div>',
      wf.matter_id
        ? '<div class="ai-wf-card-matter">Matter: ' + escapeHtml(wf.matter_id) + '</div>'
        : '',
      created
        ? '<div class="ai-wf-card-created">Created ' + created + '</div>'
        : '',
      '<div class="ai-wf-card-actions">',
        '<button class="ai-wf-del-btn" data-wf-id="' + escapeAttr(wf.id) + '" title="Delete workflow" aria-label="Delete workflow">',
          dom.TRASH_ICON,
        '</button>',
      '</div>',
    ].join('');

    card.querySelector('.ai-wf-del-btn')!.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!window.confirm('Delete workflow "' + (wf.name || 'this workflow') + '"? This cannot be undone.')) return;
      document.dispatchEvent(new CustomEvent('akili:delete-workflow', { detail: wf.id }));
    });

    list.appendChild(card);
  });
}
